import React, { useMemo } from 'react';
import { format, parseISO, getDaysInMonth } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Calendar, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { QuarterData, DayConsumptionStatus } from '@/types/piekanalyse';
import { calculateDailyKWh, calculateTotalKWh, getDayStatus, calculateThreshold } from '@/lib/piekvermoigenParser';

interface YearCalendarProps {
  rawData: Record<string, { quarters: QuarterData[] }>;
  contractedCapacity: number;
  targetSolutionKWh: number;
  onDayClick: (date: string) => void;
  year?: number;
}

const DUTCH_MONTHS = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
];

export const YearCalendar: React.FC<YearCalendarProps> = ({
  rawData,
  contractedCapacity,
  targetSolutionKWh,
  onDayClick,
  year,
}) => {
  // Calculate day statuses
  const dayStatuses = useMemo((): Record<string, DayConsumptionStatus> => {
    const statuses: Record<string, DayConsumptionStatus> = {};
    const threshold = calculateThreshold(contractedCapacity, targetSolutionKWh);
    
    Object.entries(rawData).forEach(([dateStr, dayData]) => {
      const totalKWh = calculateDailyKWh(dayData.quarters);
      statuses[dateStr] = {
        date: dateStr,
        totalKWh,
        status: getDayStatus(totalKWh, contractedCapacity, targetSolutionKWh),
        threshold,
      };
    });
    
    return statuses;
  }, [rawData, contractedCapacity, targetSolutionKWh]);

  // Detect year from data if not provided
  const detectedYear = useMemo(() => {
    if (year) return year;
    const dates = Object.keys(rawData);
    if (dates.length === 0) return new Date().getFullYear();
    return parseInt(dates[0].split('-')[0]);
  }, [rawData, year]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const statuses = Object.values(dayStatuses);
    const greenDays = statuses.filter(s => s.status === 'green').length;
    const redDays = statuses.filter(s => s.status === 'red').length;
    const totalKWh = calculateTotalKWh(rawData);
    const threshold = calculateThreshold(contractedCapacity, targetSolutionKWh);
    
    return { greenDays, redDays, totalKWh, threshold };
  }, [dayStatuses, rawData, contractedCapacity, targetSolutionKWh]);

  // Generate month grids
  const monthGrids = useMemo(() => {
    return DUTCH_MONTHS.map((monthName, monthIndex) => {
      const daysInMonth = getDaysInMonth(new Date(detectedYear, monthIndex));
      const days: (DayConsumptionStatus | null)[] = [];
      
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${detectedYear}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        days.push(dayStatuses[dateStr] || null);
      }
      
      return { monthName, monthIndex, days };
    });
  }, [detectedYear, dayStatuses]);

  const formatKWh = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)} MWh`;
    }
    return `${Math.round(value)} kWh`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Jaarkalender Levering Check
        </CardTitle>
        <div className="text-sm text-muted-foreground space-y-1">
          <p>
            Threshold: 24u × {contractedCapacity} kWp + {targetSolutionKWh.toLocaleString()} kWh = <strong>{formatKWh(summary.threshold)}/dag</strong>
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Month Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          <TooltipProvider delayDuration={100}>
            {monthGrids.map(({ monthName, monthIndex, days }) => (
              <div key={monthIndex} className="space-y-2">
                <h4 className="text-sm font-semibold text-center">{monthName}</h4>
                <div className="flex flex-wrap gap-1 justify-center">
                  {days.map((dayStatus, dayIndex) => {
                    const dateKey = dayStatus?.date || `empty-${monthIndex}-${dayIndex}`;
                    const statusClass = dayStatus 
                      ? dayStatus.status === 'green' 
                        ? 'bg-emerald-500 hover:bg-emerald-400' 
                        : 'bg-destructive hover:opacity-80'
                      : 'bg-muted/50';
                    
                    return (
                      <Tooltip key={dateKey}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => dayStatus && onDayClick(dayStatus.date)}
                            disabled={!dayStatus}
                            className={`
                              w-4 h-4 rounded-full transition-all
                              relative z-10 hover:z-20 hover:scale-125
                              ${dayStatus ? 'cursor-pointer' : 'cursor-default'}
                              ${statusClass}
                            `}
                          />
                        </TooltipTrigger>
                        {dayStatus && (
                          <TooltipContent side="top" className="text-xs z-50">
                            <p className="font-semibold">
                              {format(parseISO(dayStatus.date), 'd MMMM yyyy', { locale: nl })}
                            </p>
                            <p>Verbruik: {formatKWh(dayStatus.totalKWh)}</p>
                            <p className={dayStatus.status === 'green' ? 'text-emerald-600' : 'text-destructive'}>
                              {dayStatus.status === 'green' 
                                ? `✓ ${formatKWh(dayStatus.threshold - dayStatus.totalKWh)} onder threshold`
                                : `✗ ${formatKWh(dayStatus.totalKWh - dayStatus.threshold)} boven threshold`
                              }
                            </p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            ))}
          </TooltipProvider>
        </div>

        {/* Summary */}
        <div className="flex flex-wrap items-center gap-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span className="text-sm">
              <strong>{summary.greenDays}</strong> dagen onder threshold
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-destructive" />
            <span className="text-sm">
              <strong>{summary.redDays}</strong> dagen boven threshold
              {summary.redDays > 0 && (
                <Badge variant="destructive" className="ml-2">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Generator nodig
                </Badge>
              )}
            </span>
          </div>
          <div className="ml-auto text-sm text-muted-foreground">
            Totaal jaarverbruik: <strong>{formatKWh(summary.totalKWh)}</strong>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default YearCalendar;
